import { useState, useEffect, useMemo } from "react";
import { useAuth, useOrganization } from "@clerk/react";
import { useBriefStore, useCampaignStore } from "@/stores";
import { useNavigate, useParams } from "react-router-dom";
import posthog from "posthog-js";

import { BriefSection, PartnershipTier } from "@/types/types";
import {
  BRIEF_SECTIONS,
  FREEFORM_BRIEF_SECTION,
} from "@/constants/brief-sections";
import { buildMessageSections } from "@/constants/activation-message-sections";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  BriefHeader,
  BriefSidebar,
  BriefSectionEditor,
  FreeformEditor,
  ModeSwitchDialog,
} from "../brief";
import { Button } from "@/components/ui/button";
import { MenuIcon } from "lucide-react";

type ViewMode = "guided" | "freeform";

export default function BriefTab() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { organization } = useOrganization();

  const { campaign, campaignId, fetchCampaign } = useCampaignStore();
  const {
    brief,
    saveBrief,
    updateBriefMode,
    generateBriefSection,
    setBrief,
    includeCreatorPortal,
    updateIncludeCreatorPortal,
    isLoading,
  } = useBriefStore();

  const [title, setTitle] = useState<string>("");
  // Seed from the store when the brief is already loaded (tab switches), so a
  // freeform campaign doesn't flash the guided shell first.
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => brief?.mode ?? "guided"
  );
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);

  const { section: sectionParam = "" } = useParams<{ section: string }>();

  const tiers: PartnershipTier[] = useMemo(
    () => campaign?.partnership?.tiers ?? [],
    [campaign?.partnership?.tiers]
  );

  const messageSections = useMemo(() => buildMessageSections(tiers), [tiers]);

  // Guided edits the brief section by section; freeform is one document.
  // Both modes share the message sections, so the sidebar, the routing and
  // the message editors are identical — only the content page differs.
  const contentSections = useMemo(
    () => (viewMode === "freeform" ? [FREEFORM_BRIEF_SECTION] : BRIEF_SECTIONS),
    [viewMode]
  );
  const allSections = useMemo(
    () => [...contentSections, ...messageSections],
    [contentSections, messageSections]
  );

  // Derive current section index from URL param
  const currentSectionIdx = allSections.findIndex((s) => s.id === sectionParam);
  const currentSection = currentSectionIdx === -1 ? 0 : currentSectionIdx;
  const currentSectionData = allSections[currentSection];

  // Unknown section for this mode (also right after a mode switch) → the
  // mode's first content page.
  useEffect(() => {
    if (currentSectionIdx === -1) {
      navigate(`../${contentSections[0].id}`, { replace: true });
    }
  }, [currentSectionIdx, contentSections, navigate]);

  useEffect(() => {
    if (brief) {
      setTitle(brief.brief_title || "");
      if (brief.mode) setViewMode(brief.mode);
    }
  }, [brief]);

  const sectionValue = (sectionId: string): string =>
    String((brief as Record<string, unknown> | null)?.[sectionId] ?? "");

  const currentContent = sectionValue(currentSectionData.id);

  const handleAIGenerate = async (section: BriefSection) => {
    setAiLoading(section.id);
    const token = (await getToken()) || "";
    const content = await generateBriefSection(
      section.aiPrompt,
      currentContent,
      campaignId || "",
      token
    );
    if (content) {
      await handleSectionSave(section.id, content);
      setBrief({ ...(brief ?? {}), [section.id]: content });
    }
    setAiLoading(null);
  };

  const handleSectionSave = async (sectionId: string, content: string) => {
    setSaving(true);
    await saveBrief(campaignId, content, sectionId, (await getToken()) || "");
    if (brief) setBrief({ ...brief, [sectionId]: content });
    setSaving(false);
  };

  const handleTitleSave = async () => {
    if (title !== brief?.brief_title) {
      setSaving(true);
      await saveBrief(
        campaignId,
        title,
        "brief_title",
        (await getToken()) || ""
      );
      setSaving(false);
    }
  };

  const handleFreeformSwitch = () => {
    const hasContent = allSections.some(
      (section) => sectionValue(section.id).length > 20
    );
    if (hasContent) {
      setShowSwitchDialog(true);
    } else {
      switchMode("freeform");
    }
  };

  const switchMode = async (newMode: ViewMode) => {
    setSaving(true);

    try {
      const token = (await getToken()) || "";

      if (newMode === "freeform" && viewMode === "guided") {
        const combinedContent = allSections
          .filter(
            (section) =>
              !section.id.startsWith("activation_message_tier_") &&
              section.id !== "rejection_message"
          )
          .map((section) => {
            const content = sectionValue(section.id);
            return content.trim() ? `<h2>${section.title}</h2>\n${content}` : "";
          })
          .filter(Boolean)
          .join("\n\n");

        if (combinedContent) {
          await saveBrief(campaignId, combinedContent, "brief_content", token);
        }
      } else if (newMode === "guided" && viewMode === "freeform") {
        await saveBrief(campaignId, "", "brief_content", token);
      }

      // Update mode first, then fetch
      await updateBriefMode(campaignId, newMode, token);
      setViewMode(newMode);

      if (campaignId && organization?.id) {
        await fetchCampaign(campaignId, organization?.id, token);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error switching mode:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateIncludeCreatorPortal = async (value: boolean) => {
    const token = (await getToken()) || "";
    if (!campaignId || !token) return;
    await updateIncludeCreatorPortal(campaignId, value, token);
  };

  const isSectionComplete = (sectionId: string) =>
    sectionValue(sectionId).length > 20;

  const lastContentIdx = contentSections.length - 1;
  const isLastSection = currentSection === lastContentIdx;

  const handleNextSection = async () => {
    if (currentContent) {
      await handleSectionSave(currentSectionData.id, currentContent);
    }
    if (currentSection < lastContentIdx) {
      navigate(`../${allSections[currentSection + 1].id}`);
    } else {
      posthog.capture("brief_completed", {
        campaignId,
      });
      navigate({
        pathname: `/dashboard/campaigns/${campaignId}/creators`,
      });
    }
  };

  const isFreeformContent = currentSectionData.id === FREEFORM_BRIEF_SECTION.id;
  const isActivationMessage = currentSectionData.id.startsWith(
    "activation_message_tier_"
  );
  const isMessageSection =
    isActivationMessage || currentSectionData.id === "rejection_message";
  const showAIButton =
    !isActivationMessage && currentSectionData.id !== "content_references";

  const sidebar = (className: string) => (
    <BriefSidebar
      briefSections={contentSections}
      briefGroupTitle={viewMode === "freeform" ? "Brief" : "Brief Sections"}
      messageSections={messageSections}
      currentSectionId={sectionParam}
      onSectionChange={(id) => navigate(`../${id}`)}
      isSectionComplete={isSectionComplete}
      className={className}
    />
  );

  return (
    <>
      <div className="flex flex-col h-full">
        <BriefHeader
          title={title}
          setTitle={setTitle}
          onTitleSave={handleTitleSave}
          saving={saving}
          viewMode={viewMode}
          onFreeformSwitch={handleFreeformSwitch}
          onGuidedSwitch={() => switchMode("guided")}
          campaignId={campaignId || ""}
        />
        <div className="flex-1 min-h-0 flex lg:flex-row flex-col">
          {sidebar("lg:block hidden")}
          <Sheet>
            <SheetTrigger className="lg:hidden block shrink-0 w-auto mr-auto">
              <Button variant="outline" size="sm" className="border-t-0 border-l-0 rounded-tl-none rounded-bl-none rounded-tr-none rounded-br-lg shadow-xs" scope={false}>
                <MenuIcon /> Sections
              </Button>
            </SheetTrigger>
            <SheetContent className="pt-10 h-full w-min" side="left">
              {sidebar("border-0 max-w-72")}
              <SheetClose className="mt-auto p-2">
                <Button variant="outline" className="w-full">
                  Close
                </Button>
              </SheetClose>
            </SheetContent>
          </Sheet>
          <div className="flex-1 min-h-0 md:p-5 p-2 overflow-auto">
            {isFreeformContent ? (
              <FreeformEditor
                section={currentSectionData}
                content={currentContent}
                onSave={(content) =>
                  handleSectionSave(currentSectionData.id, content)
                }
                onClear={() => {
                  setBrief({ ...brief, [currentSectionData.id]: "" });
                  handleSectionSave(currentSectionData.id, "");
                }}
                onComplete={handleNextSection}
                saving={saving}
              />
            ) : (
              <BriefSectionEditor
                section={currentSectionData}
                content={currentContent}
                onChange={(content) =>
                  setBrief({ ...brief, [currentSectionData.id]: content })
                }
                onSave={(content) =>
                  handleSectionSave(currentSectionData.id, content)
                }
                onClear={() => {
                  setBrief({ ...brief, [currentSectionData.id]: "" });
                  handleSectionSave(currentSectionData.id, "");
                }}
                onAIGenerate={() => handleAIGenerate(currentSectionData)}
                onNext={handleNextSection}
                aiLoading={aiLoading === currentSectionData.id}
                saving={saving}
                isLastSection={isLastSection}
                isActivationMessage={isActivationMessage}
                hideNavigation={isMessageSection}
                showAIButton={showAIButton}
                includeCreatorPortal={includeCreatorPortal}
                onTogglePortal={handleUpdateIncludeCreatorPortal}
                isPortalLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
      <ModeSwitchDialog
        open={showSwitchDialog}
        onOpenChange={setShowSwitchDialog}
        allSections={allSections}
        onConfirm={() => switchMode("freeform")}
      />
    </>
  );
}
